import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ResourceNotes } from '@/components/notes/ResourceNotes';
import {
  Flag,
  Lock,
  History,
  FileQuestion,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ShieldOff,
  Award,
  XCircle,
} from 'lucide-react';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ScoreGauge } from '@/components/shared/ScoreGauge';
import {
  useAssessment,
  useAssessmentHistory,
  useBeginReview,
  useFlagQuestion,
  useAddBypassOverride,
  useCancelAssessment,
} from '@/api/hooks/useAssessments';
import { useVendorCertifications } from '@/api/hooks/useVendors';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/lib/utils';
import { BackLink } from '@/components/layout/BackLink';
import { AssessmentLifecycleBand } from '@/components/assessment/AssessmentLifecycleBand';
import type { AssessmentSection, AssessmentQuestion, Certification } from '@/api/types/assessment.types';

// ── Question type badge ───────────────────────────────────────────────────────

function QuestionTypeBadge({ type }: { type: string }) {
  const map: Record<string, { label: string; variant: 'muted' | 'sky' | 'violet' | 'default' }> = {
    multiple_choice: { label: 'Multiple Choice', variant: 'sky' },
    free_text: { label: 'Free Text', variant: 'muted' },
    file_upload: { label: 'File Upload', variant: 'violet' },
    yes_no: { label: 'Yes / No', variant: 'default' },
  };
  const { label, variant } = map[type] ?? { label: type, variant: 'muted' };
  return <Badge variant={variant} className="text-[10px] py-0">{label}</Badge>;
}

// ── Response display ──────────────────────────────────────────────────────────

function ResponseDisplay({ question }: { question: AssessmentQuestion }) {
  const r = question.response;
  if (!r) return <p className="text-sm text-text-secondary italic">No response yet</p>;

  if (question.question_type === 'yes_no') {
    return (
      <span className={cn('text-sm font-medium', r.response_boolean ? 'text-accent-emerald' : 'text-accent-rose')}>
        {r.response_boolean == null ? '—' : r.response_boolean ? 'Yes' : 'No'}
      </span>
    );
  }
  if (question.question_type === 'multiple_choice') {
    return (
      <div className="flex flex-wrap gap-1.5">
        {(r.response_options ?? []).map((opt) => (
          <Badge key={opt} variant="muted">{opt}</Badge>
        ))}
      </div>
    );
  }
  if (question.question_type === 'file_upload') {
    return r.file_url ? (
      <a href={r.file_url} target="_blank" rel="noopener noreferrer" className="text-sm text-accent-cyan hover:underline">
        View uploaded file
      </a>
    ) : <p className="text-sm text-text-secondary italic">No file uploaded</p>;
  }
  return <p className="text-sm text-text-primary whitespace-pre-wrap">{r.response_text ?? '—'}</p>;
}

// ── Question card ─────────────────────────────────────────────────────────────

function QuestionCard({ question, assessmentId, canAct, canOverride, isBypassApplied, bypassedCerts }: { question: AssessmentQuestion; assessmentId: string; canAct: boolean; canOverride: boolean; isBypassApplied?: boolean; bypassedCerts?: Certification[] }) {
  const r = question.response;
  const isBypassed = r?.is_auto_scored === true && isBypassApplied === true;
  const flagMutation = useFlagQuestion(assessmentId);
  const overrideMutation = useAddBypassOverride(assessmentId);
  const [overrideNotes, setOverrideNotes] = useState('');
  const [showOverrideInput, setShowOverrideInput] = useState(false);

  return (
    <div className={cn('rounded-lg border p-4 space-y-3', r?.is_flagged ? 'border-accent-rose/40 bg-accent-rose/5' : 'border-border-subtle bg-bg-surface')}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <QuestionTypeBadge type={question.question_type} />
            {r?.is_flagged && (
              <Badge variant="rose" className="text-[10px]">Flagged</Badge>
            )}
            {r?.is_sent_back && (
              <Badge variant="amber" className="text-[10px]">Sent Back</Badge>
            )}
            {r?.is_auto_scored === true && isBypassApplied && (
              <StatusBadge status="bypassed" label="Bypassed by certification" className="text-[10px] py-0" />
            )}
          </div>
          {r?.is_auto_scored === true && isBypassApplied && bypassedCerts && bypassedCerts.length > 0 && (
            <p className="text-xs text-text-secondary">
              {bypassedCerts[0].cert_type}
              {bypassedCerts[0].issuing_body ? ` · ${bypassedCerts[0].issuing_body}` : ''}
              {bypassedCerts[0].expiry_date ? ` · valid through ${format(parseISO(bypassedCerts[0].expiry_date), 'MMM d, yyyy')}` : ''}
            </p>
          )}
          <p className="text-sm font-medium text-text-primary">{question.question_text}</p>
        </div>
        {/* Score bar */}
        {r?.score_awarded != null && !isBypassed && (
          <div className="shrink-0 text-right">
            <p className="text-xs text-text-secondary font-mono">
              {r.score_awarded} / {question.max_score}
            </p>
            <div className="mt-1 h-1.5 w-20 rounded-full bg-bg-surface-2">
              <div
                className="h-full rounded-full bg-accent-cyan"
                style={{ width: `${Math.min(100, (r.score_awarded / question.max_score) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Sent back analyst note */}
      {r?.is_sent_back && r.analyst_note && (
        <div className="rounded-md border border-accent-amber/30 bg-accent-amber/10 px-3 py-2">
          <p className="text-xs font-medium text-accent-amber mb-1">Analyst note:</p>
          <p className="text-sm text-text-primary">{r.analyst_note}</p>
        </div>
      )}

      {/* Response */}
      <div className="border-t border-border-subtle pt-3">
        {isBypassed ? (
          <p className="text-xs text-text-secondary italic">Credited via certification bypass</p>
        ) : (
          <>
            <p className="text-xs font-medium text-text-secondary mb-1.5">Response</p>
            <ResponseDisplay question={question} />
          </>
        )}
      </div>

      {/* Vendor comment */}
      {r?.vendor_clarification && (
        <div className="border-t border-border-subtle pt-3">
          <p className="text-xs font-medium text-text-secondary mb-1">Vendor comment</p>
          <p className="text-sm text-text-primary whitespace-pre-wrap">{r.vendor_clarification}</p>
        </div>
      )}

      {/* Analyst actions */}
      {(canAct || canOverride) && (
        <div className="space-y-2 pt-1 border-t border-border-subtle">
          <div className="flex gap-2">
            {canAct && (
              <Button
                variant="ghost"
                size="sm"
                className={cn('h-7 text-xs', r?.is_flagged ? 'text-accent-rose' : 'text-text-secondary')}
                onClick={() => r && flagMutation.mutate({ responseId: r.id, flagged: !r.is_flagged })}
                disabled={flagMutation.isPending || !r}
              >
                <Flag className="h-3.5 w-3.5 mr-1" />
                {r?.is_flagged ? 'Unflag' : 'Flag'}
              </Button>
            )}
            {canOverride && !showOverrideInput && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-text-secondary"
                onClick={() => setShowOverrideInput(true)}
                disabled={overrideMutation.isPending}
              >
                <ShieldOff className="h-3.5 w-3.5 mr-1" />
                Require Answer
              </Button>
            )}
          </div>
          {canOverride && showOverrideInput && (
            <div className="space-y-2 pl-1">
              <p className="text-xs text-text-secondary">Require the vendor to answer this question despite any certification coverage.</p>
              <input
                type="text"
                value={overrideNotes}
                onChange={(e) => setOverrideNotes(e.target.value)}
                placeholder="Reason (optional)"
                className="w-full rounded border border-border-subtle bg-bg-surface-2 px-2 py-1 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-cyan"
              />
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={() => overrideMutation.mutate(
                    { questionId: question.id, notes: overrideNotes || undefined },
                    { onSuccess: () => { setShowOverrideInput(false); setOverrideNotes(''); toast.success('Answer required despite certification.'); }, onError: () => toast.error('Failed to apply override.') },
                  )}
                  disabled={overrideMutation.isPending}
                >
                  {overrideMutation.isPending ? 'Saving…' : 'Confirm'}
                </Button>
                <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => { setShowOverrideInput(false); setOverrideNotes(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Section accordion ─────────────────────────────────────────────────────────

function SectionAccordion({ section, assessmentId, canAct, canOverride, defaultOpen, isBypassApplied, bypassedCerts }: {
  section: AssessmentSection;
  assessmentId: string;
  canAct: boolean;
  canOverride: boolean;
  defaultOpen: boolean;
  isBypassApplied?: boolean;
  bypassedCerts?: Certification[];
}) {
  const [open, setOpen] = useState(defaultOpen);
  const pct = section.max_section_score && section.max_section_score > 0
    ? Math.round(((section.section_score ?? 0) / section.max_section_score) * 100)
    : null;

  return (
    <div className="rounded-lg border border-border-subtle overflow-hidden">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex w-full items-center justify-between px-4 py-3 bg-bg-surface hover:bg-bg-surface-2 transition-colors"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="h-4 w-4 text-text-secondary" /> : <ChevronRight className="h-4 w-4 text-text-secondary" />}
          <span className="font-medium text-text-primary text-sm">{section.name}</span>
          <Badge variant="muted" className="text-[10px] font-mono">{section.questions.length} Q</Badge>
        </div>
        {pct != null && (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-24 rounded-full bg-bg-surface-2">
              <div className="h-full rounded-full bg-accent-cyan" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-mono text-text-secondary">{pct}%</span>
          </div>
        )}
      </button>
      {open && (
        <div className="p-4 space-y-3 bg-bg-base/30">
          {section.questions.map((q) => (
            <QuestionCard key={q.id} question={q} assessmentId={assessmentId} canAct={canAct} canOverride={canOverride} isBypassApplied={isBypassApplied} bypassedCerts={bypassedCerts} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── History tab ───────────────────────────────────────────────────────────────

function HistoryTab({ assessmentId }: { assessmentId: string }) {
  const { data: events, isLoading } = useAssessmentHistory(assessmentId);

  if (isLoading) {
    return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  }

  if (!events || events.length === 0) {
    return <p className="py-8 text-center text-sm text-text-secondary">No history events yet</p>;
  }

  return (
    <div className="space-y-1">
      {events.map((event) => (
        <div key={event.id} className="flex gap-3 rounded-lg p-3 hover:bg-bg-surface-2 transition-colors">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-bg-surface-2">
            <History className="h-3.5 w-3.5 text-text-secondary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-text-primary">{event.description}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {event.performed_by_name && (
                <span className="text-xs text-text-secondary">{event.performed_by_name}</span>
              )}
              <span className="text-xs text-text-secondary">
                {formatDistanceToNow(parseISO(event.created_at), { addSuffix: true })}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function AssessmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasAnyRole } = useAuthStore();
  const { data: assessment, isLoading } = useAssessment(id);
  const { data: vendorCerts = [] } = useVendorCertifications(assessment?.vendor_id);
  const bypassedCerts = vendorCerts.filter((c) => (assessment?.bypassed_cert_ids ?? []).includes(c.id));
  const isBypassApplied = assessment?.certification_bypass_applied === true;

  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const beginReviewMutation = useBeginReview(id!);
  const cancelMutation = useCancelAssessment(id!);

  const isAnalyst = hasAnyRole(['analyst', 'risk_manager', 'admin', 'super_admin']);
  const isRiskManager = hasAnyRole(['risk_manager', 'admin', 'super_admin']); // F8-1
  const canAct = isAnalyst && (assessment?.status === 'under_review' || assessment?.status === 'resubmitted');
  const canApproveOrSendBack = isRiskManager && (assessment?.status === 'under_review' || assessment?.status === 'resubmitted'); // F8-1
  const canBeginReview = isAnalyst && assessment?.status === 'submitted';
  const canResend = isRiskManager && ['issued', 'in_progress', 'returned', 'resubmitted'].includes(assessment?.status ?? '');
  // Cancel: RM, and only from an ACTIVE state (server-enforced too via assertLegalTransition). Locked once
  // approved; terminal (rejected/expired/cancelled) + draft are not cancellable.
  const canCancel = isRiskManager && ['issued', 'in_progress', 'submitted', 'under_review', 'returned', 'resubmitted', 'reopened'].includes(assessment?.status ?? '');
  // canOverride: risk_manager can require answers despite cert coverage on active assessments
  const canOverride = isRiskManager && (assessment?.status === 'issued' || assessment?.status === 'in_progress' || assessment?.status === 'under_review' || assessment?.status === 'resubmitted');

  const flaggedQuestionIds = (assessment?.sections ?? [])
    .flatMap((s) => s.questions)
    .filter((q) => q.response?.is_flagged)
    .map((q) => q.id);

  const bypassedQuestionIds = (assessment?.sections ?? [])
    .flatMap((s) => s.questions)
    .filter((q) => q.response?.is_cert_bypassed)
    .map((q) => q.id);

  const allBypassedCertsApproved = isBypassApplied &&
    bypassedCerts.length > 0 &&
    bypassedCerts.every((c) => c.review_status === 'accepted');

  // Lazy-load modals
  const [SendBackModal, setSendBackModal] = useState<React.ComponentType<{ open: boolean; onClose: () => void; assessmentId: string; preselectedQuestionIds?: string[] }> | null>(null);
  const [ApproveModal, setApproveModal] = useState<React.ComponentType<{ open: boolean; onClose: () => void; assessment: typeof assessment }> | null>(null);
  const [BypassRiskModal, setBypassRiskModal] = useState<React.ComponentType<{ open: boolean; onClose: () => void; assessment: typeof assessment }> | null>(null);
  const [bypassRiskOpen, setBypassRiskOpen] = useState(false);
  const [BypassRiskWarningModal, setBypassRiskWarningModal] = useState<React.ComponentType<{
    open: boolean; onClose: () => void; onSendBack: () => void; onAcceptRisk: () => void;
    assessment: typeof assessment; bypassedCerts: typeof bypassedCerts;
  }> | null>(null);
  const [bypassRiskWarningOpen, setBypassRiskWarningOpen] = useState(false);
  const [ResendModal, setResendModal] = useState<React.ComponentType<{ open: boolean; onClose: () => void; assessmentId: string; vendorId: string; existingRecipientEmails: string[] }> | null>(null);
  const [resendOpen, setResendOpen] = useState(false);

  async function openResend() {
    if (!ResendModal) {
      const mod = await import('@/features/assessments/ResendAssessmentModal');
      setResendModal(() => mod.ResendAssessmentModal);
    }
    setResendOpen(true);
  }

  async function openSendBack() {
    if (!SendBackModal) {
      const mod = await import('@/features/assessments/SendBackModal');
      setSendBackModal(() => mod.SendBackModal);
    }
    setSendBackOpen(true);
  }

  async function openApprove() {
    if (!ApproveModal) {
      const mod = await import('@/features/assessments/ApproveAssessmentModal');
      setApproveModal(() => mod.ApproveAssessmentModal);
    }
    setApproveOpen(true);
  }

  async function openBypassRiskApprove() {
    if (!BypassRiskModal) {
      const mod = await import('@/features/assessments/BypassRiskAcceptanceModal');
      setBypassRiskModal(() => mod.BypassRiskAcceptanceModal);
    }
    setBypassRiskOpen(true);
  }

  async function openBypassRiskWarning() {
    if (!BypassRiskWarningModal) {
      const mod = await import('@/features/assessments/BypassRiskWarningModal');
      setBypassRiskWarningModal(() => mod.BypassRiskWarningModal);
    }
    setBypassRiskWarningOpen(true);
  }

  async function handleApproveClick() {
    if (!isBypassApplied || allBypassedCertsApproved) {
      await openApprove();
    } else {
      await openBypassRiskWarning();
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <AlertTriangle className="h-10 w-10 text-accent-rose/60" />
        <p className="text-text-secondary">Assessment not found</p>
        <Button variant="outline" onClick={() => navigate('/assessments')}>Back to assessments</Button>
      </div>
    );
  }

  const showScore = ['submitted', 'under_review', 'returned', 'resubmitted', 'approved', 'reopened'].includes(assessment.status) && assessment.final_score != null;

  return (
    <div className="space-y-6">
      {/* Back to the vendor this assessment belongs to (vendor_id always present). */}
      <BackLink to={`/vendors/${assessment.vendor_id}`} label={assessment.vendor_name ?? 'Vendor'} />

      {/* Header card */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={assessment.status} />
                {assessment.vendor_tier && (
                  <StatusBadge
                    status={assessment.vendor_tier}
                    label={assessment.vendor_tier.replace('tier_', 'T').replace('_critical', '').replace('_high', '').replace('_medium', '').replace('_low', '')}
                  />
                )}
              </div>
              <h1 className="text-xl font-bold text-text-primary">
                {assessment.vendor_name ?? 'Unknown Vendor'}
              </h1>
              <p className="text-sm text-text-secondary">{assessment.template_name ?? assessment.title}</p>
              <div className="flex flex-wrap gap-4 text-xs text-text-secondary">
                {assessment.issued_at && (
                  <span>Issued {format(parseISO(assessment.issued_at), 'MMM d, yyyy')}</span>
                )}
                {assessment.due_date && (
                  <span className={cn(
                    new Date() > parseISO(assessment.due_date) && assessment.status !== 'approved' ? 'text-accent-rose font-medium' : '',
                  )}>
                    Due {format(parseISO(assessment.due_date), 'MMM d, yyyy')}
                  </span>
                )}
                {assessment.analyst_name && <span>Analyst: {assessment.analyst_name}</span>}
              </div>
            </div>
            {/* Score gauge */}
            {showScore && (
              <ScoreGauge score={assessment.final_score} size="md" showLabel max={100} />
            )}
          </div>

          {/* Frozen indicator (D-VA-P2-21) */}
          {assessment?.evidence_frozen_at && (
            <div className="flex items-center gap-1.5 mt-3 text-xs text-text-secondary">
              <Lock className="h-3.5 w-3.5 text-accent-amber" />
              <span>Evidence frozen — responses locked</span>
            </div>
          )}

          {/* Action buttons */}
          {(isAnalyst || isRiskManager) && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border-subtle">
              {canBeginReview && (
                <Button
                  size="sm"
                  onClick={() => beginReviewMutation.mutate()}
                  disabled={beginReviewMutation.isPending}
                >
                  {beginReviewMutation.isPending ? 'Starting…' : 'Begin Review'}
                </Button>
              )}
              {canApproveOrSendBack && (
                <>
                  <Button size="sm" variant="outline" className="border-accent-emerald/40 text-accent-emerald hover:bg-accent-emerald/10" onClick={handleApproveClick}>
                    <CheckCircle2 className="h-4 w-4 mr-1.5" />
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" className="border-accent-amber/40 text-accent-amber hover:bg-accent-amber/10" onClick={openSendBack}>
                    <AlertTriangle className="h-4 w-4 mr-1.5" />
                    Send Back
                  </Button>
                </>
              )}
              {canResend && (
                <Button size="sm" variant="outline" className="border-accent-sky/40 text-accent-sky hover:bg-accent-sky/10" onClick={openResend}>
                  <RefreshCw className="h-4 w-4 mr-1.5" />
                  Resend
                </Button>
              )}
              {canCancel && (
                <Button size="sm" variant="outline" className="border-accent-rose/40 text-accent-rose hover:bg-accent-rose/10 ml-auto"
                  onClick={() => { setCancelReason(''); setCancelOpen(true); }}>
                  <XCircle className="h-4 w-4 mr-1.5" />
                  Cancel Assessment
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel confirm dialog — required reason; RM only; active states only (server-enforced too) */}
      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !cancelMutation.isPending && setCancelOpen(false)}>
          <div className="w-full max-w-md rounded-lg border border-border-subtle bg-bg-surface p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-accent-rose shrink-0" />
              <h3 className="text-base font-semibold text-text-primary">Cancel assessment</h3>
            </div>
            <p className="text-sm text-text-secondary">
              This abandons the assessment — the vendor’s work stops here and it moves to Cancelled. A reason is required and recorded in the audit trail.
            </p>
            <div>
              <label className="text-xs font-medium text-text-secondary">Reason <span className="text-accent-rose">*</span></label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                placeholder="Why is this assessment being cancelled?"
                className="mt-1 w-full rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:border-accent-cyan"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCancelOpen(false)} disabled={cancelMutation.isPending}>Keep assessment</Button>
              <Button size="sm" className="border-accent-rose/40 text-accent-rose hover:bg-accent-rose/10" variant="outline"
                disabled={cancelReason.trim().length < 3 || cancelMutation.isPending}
                onClick={() => cancelMutation.mutate({ reason: cancelReason.trim() }, {
                  onSuccess: () => { setCancelOpen(false); toast.success('Assessment cancelled'); },
                  onError: () => toast.error('Could not cancel the assessment'),
                })}>
                {cancelMutation.isPending ? 'Cancelling…' : 'Cancel assessment'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Certification Attestation banner */}
      {isBypassApplied && (
        <div className="rounded-lg border border-accent-cyan/30 bg-accent-cyan/5 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-accent-cyan shrink-0" />
            <span className="text-sm font-medium text-text-primary">Certification Attestation</span>
          </div>
          <p className="text-sm text-text-secondary">
            {assessment.questions_bypassed_count ?? 0}{' '}
            question{(assessment.questions_bypassed_count ?? 0) !== 1 ? 's' : ''} bypassed via{' '}
            {(assessment.bypassed_cert_ids ?? []).length}{' '}
            certification{(assessment.bypassed_cert_ids ?? []).length !== 1 ? 's' : ''}
          </p>
          {bypassedCerts.length > 0 && (
            <div className="space-y-2">
              {bypassedCerts.map((cert) => (
                <div key={cert.id} className="flex flex-wrap items-center gap-x-1.5 text-xs text-text-secondary">
                  <span className="font-medium text-text-primary">{cert.cert_type}</span>
                  {cert.issuing_body && <span>· {cert.issuing_body}</span>}
                  {cert.expiry_date && (
                    <span>· valid through {format(parseISO(cert.expiry_date), 'MMM d, yyyy')}</span>
                  )}
                  <StatusBadge status={cert.review_status ?? 'not_reviewed'} className="text-[10px] h-4 px-1.5" />
                  <button
                    type="button"
                    className="text-accent-cyan hover:underline"
                    onClick={() => navigate(`/vendors/${assessment.vendor_id}`)}
                  >
                    Review →
                  </button>
                </div>
              ))}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-accent-cyan px-0 hover:bg-transparent"
            onClick={() => navigate(`/vendors/${assessment.vendor_id}`)}
          >
            View Details →
          </Button>
        </div>
      )}

      {/* Lifecycle-adaptive visualization band (assessment-viz Surface 1) — above the tabs */}
      <AssessmentLifecycleBand assessment={assessment} />

      {/* Tabs */}
      <Tabs defaultValue="questions">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="questions" className="gap-1.5">
            <FileQuestion className="h-4 w-4" /> Questions
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5">
            <History className="h-4 w-4" /> History
          </TabsTrigger>
        </TabsList>

        {/* Questions */}
        <TabsContent value="questions" className="space-y-3 mt-4">
          {assessment.sections && assessment.sections.length > 0 ? (
            assessment.sections.map((section, idx) => (
              <SectionAccordion
                key={section.id}
                section={section}
                assessmentId={assessment.id}
                canAct={canAct}
                canOverride={canOverride}
                defaultOpen={idx === 0}
                isBypassApplied={isBypassApplied}
                bypassedCerts={bypassedCerts}
              />
            ))
          ) : (
            <p className="py-8 text-center text-sm text-text-secondary">No questions available</p>
          )}
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Audit Trail</CardTitle>
            </CardHeader>
            <CardContent>
              <HistoryTab assessmentId={assessment.id} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── COLLAB-NOTIF S2: Notes ───────────────────────────────────────── */}
      <div className="mt-6">
        <ResourceNotes resourceType="assessment" resourceId={assessment.id} host="detail" resourceLabel="assessment" />
      </div>

      {/* Lazy modals */}
      {SendBackModal && (
        <SendBackModal open={sendBackOpen} onClose={() => setSendBackOpen(false)} assessmentId={assessment.id} preselectedQuestionIds={isBypassApplied ? bypassedQuestionIds : flaggedQuestionIds} />
      )}
      {ApproveModal && (
        <ApproveModal open={approveOpen} onClose={() => setApproveOpen(false)} assessment={assessment} />
      )}
      {BypassRiskModal && (
        <BypassRiskModal open={bypassRiskOpen} onClose={() => setBypassRiskOpen(false)} assessment={assessment} />
      )}
      {BypassRiskWarningModal && (
        <BypassRiskWarningModal
          open={bypassRiskWarningOpen}
          onClose={() => setBypassRiskWarningOpen(false)}
          onSendBack={() => { setBypassRiskWarningOpen(false); openSendBack(); }}
          onAcceptRisk={() => { setBypassRiskWarningOpen(false); openBypassRiskApprove(); }}
          assessment={assessment}
          bypassedCerts={bypassedCerts}
        />
      )}
      {ResendModal && (
        <ResendModal
          open={resendOpen}
          onClose={() => setResendOpen(false)}
          assessmentId={assessment.id}
          vendorId={assessment.vendor_id}
          existingRecipientEmails={assessment.recipient_emails ?? []}
        />
      )}
    </div>
  );
}
